import { TARGET_OUTLINE_STYLE, WIDGET_STYLES } from "./styles/widget-styles.generated";
import { createWidgetStore } from "./store/widget-store";
import {
  type CaptureProvider,
  type WidgetConfig,
  type WidgetController,
  type WidgetDeps,
} from "./store/widget-types";
import { createDrawController } from "./utils/widget-draw";
import { watchWidgetLayer } from "./utils/widget-layer";
import { watchPageNavigation } from "./utils/page-navigation";
import { mountWidgetRoot } from "./ui/mount-widget-root";

export type { CaptureProvider, WidgetConfig, WidgetController, WidgetDeps };

const HOST_ATTR = "data-previewloop";

let mountedTeardown: (() => void) | null = null;

export function disposeMountedWidget(): void {
  mountedTeardown?.();
  mountedTeardown = null;
}

const ensureTargetOutlineStyle = (): void => {
  if (document.querySelector("[data-pl-target-style]")) return;
  const style = document.createElement("style");
  style.setAttribute("data-pl-target-style", "");
  style.textContent = TARGET_OUTLINE_STYLE;
  document.head.appendChild(style);
};

export function mountWidget(config: WidgetConfig, deps: WidgetDeps = {}): WidgetController {
  disposeMountedWidget();

  const existing = document.querySelector<HTMLElement>(`[${HOST_ATTR}]`);
  if (existing?.shadowRoot) {
    return {
      host: existing,
      shadowRoot: existing.shadowRoot,
      config,
      isCommentMode: () => false,
      enterCommentMode: () => {},
      exitCommentMode: () => {},
      getCollectedCount: () => 0,
      isDrawing: () => false,
      hasDrawing: () => false,
      dispose: () => {},
    };
  }

  ensureTargetOutlineStyle();
  const capture = deps.captureScreenshot ?? (async () => new Blob([], { type: "image/png" }));

  const host = document.createElement("div");
  host.setAttribute(HOST_ATTR, "");
  // Host-app modal layers (Radix and friends) disable pointer events on the
  // body and mark its other children aria-hidden/inert while a menu or dialog
  // is open, which makes the launcher unreachable (#114). Pointer events are
  // reasserted declaratively by the injected page stylesheet (!important beats
  // both the body-level none and an inline none on the host); the observer
  // strips the accessibility marks. Bounded so a host library that fights
  // back degrades to a hidden launcher instead of an observer ping-pong loop.
  let hostGuardCorrections = 0;
  const hostGuard = new MutationObserver(() => {
    if (++hostGuardCorrections > 60) {
      hostGuard.disconnect();
      return;
    }
    if (host.hasAttribute("aria-hidden")) host.removeAttribute("aria-hidden");
    if (host.hasAttribute("inert")) host.removeAttribute("inert");
    hostGuard.takeRecords();
  });
  hostGuard.observe(host, {
    attributes: true,
    attributeFilter: ["aria-hidden", "inert"],
  });
  const shadowRoot = host.attachShadow({ mode: "open" });

  const style = document.createElement("style");
  style.textContent = WIDGET_STYLES;

  // The extension fades this root out (opacity, which no descendant can
  // override) while it grabs real pixels; the slotted draw canvas stays put.
  const uiRoot = document.createElement("div");
  uiRoot.setAttribute("data-pl-ui", "");
  shadowRoot.append(style, document.createElement("slot"), uiRoot);
  document.body.appendChild(host);
  const unwatchLayer = watchWidgetLayer(host);

  const drawHooks = {
    escape: (): void => {},
    strokeEnd: (_point: { x: number; y: number }): void => {},
  };
  const draw = createDrawController({
    surfaceRoot: host,
    onEscape: () => drawHooks.escape(),
    onStrokeEnd: (point) => drawHooks.strokeEnd(point),
  });

  const store = createWidgetStore({
    config,
    deps,
    host,
    draw,
    capture,
  });
  drawHooks.escape = () => store.setDrawMode(false);
  drawHooks.strokeEnd = (point) => store.annotateFromDraw(point);

  const disposeUi = mountWidgetRoot(uiRoot, store);
  const unwatchNavigation = watchPageNavigation(() => store.onPageChange());

  const dispose = (): void => {
    unwatchNavigation();
    unwatchLayer();
    hostGuard.disconnect();
    store.dispose();
    disposeUi();
    host.remove();
    if (mountedTeardown === dispose) mountedTeardown = null;
  };
  mountedTeardown = dispose;

  return {
    host,
    shadowRoot,
    config,
    isCommentMode: () => store.commentMode(),
    enterCommentMode: () => store.enterCommentMode(),
    exitCommentMode: () => store.exitCommentMode(),
    getCollectedCount: () => store.collected().length,
    isDrawing: () => store.drawMode(),
    hasDrawing: () => draw.hasDrawing(),
    dispose,
  };
}
